import * as XLSX from 'xlsx'
import type { OrgBranch, OrgCR } from '@/types/organization'

// ── Row shape (same contract as before, just transport format changed) ────────

export interface XlsxRow {
	filial: string
	codigo_filial?: string
	codigo_cr: string
	nome_cr: string
}

// ── Diff Types ────────────────────────────────────────────────────────────────

export interface CrMovedDiff {
	type: 'CR_MOVED'
	crId: string
	crCode: string
	crName: string
	fromBranch: string
	toBranch: string
	action:
		| { kind: 'MOVE_TO_VIRTUAL'; targetBranchId: string }
		| { kind: 'MOVE_TO_NEW_VIRTUAL'; branchName: string }
		| { kind: 'UNDO_MOVE' }
}

export interface BranchCreatedDiff {
	type: 'BRANCH_CREATED'
	branchName: string
	crs: XlsxRow[]
}

export interface BranchRenamedDiff {
	type: 'BRANCH_RENAMED'
	branchId: string
	branchCode: string | null
	fromName: string
	toName: string
}

export interface CrRenamedDiff {
	type: 'CR_RENAMED'
	crId: string
	crCode: string
	fromName: string
	toName: string
}

export interface CrNotFoundDiff {
	type: 'CR_NOT_FOUND'
	crCode: string
	crName: string
	filial: string
}

export interface OrgDiffResult {
	newBranches: BranchCreatedDiff[]
	renamedBranches: BranchRenamedDiff[]
	renamedCRs: CrRenamedDiff[]
	movedCRs: CrMovedDiff[]
	notFound: CrNotFoundDiff[]
	unchangedCount: number
}

// ── XLSX Generation & Download ────────────────────────────────────────────────

export function downloadOrgXlsx(
	branches: OrgBranch[],
	filename = 'org-structure.xlsx',
): void {
	const rows: {
		Filial: string
		Código_Filial: string
		Código_CR: string
		Nome_CR: string
	}[] = []

	for (const branch of branches) {
		for (const cr of branch.crs) {
			rows.push({
				Filial: branch.name,
				Código_Filial: branch.code ?? '',
				Código_CR: cr.code ?? '',
				Nome_CR: cr.name,
			})
		}
	}

	const worksheet = XLSX.utils.json_to_sheet(rows)

	// Column widths
	worksheet['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 14 }, { wch: 50 }]

	const workbook = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Estrutura Org')

	XLSX.writeFile(workbook, filename)
}

// ── XLSX Parsing ──────────────────────────────────────────────────────────────

export function parseOrgXlsx(buffer: ArrayBuffer): XlsxRow[] {
	const workbook = XLSX.read(buffer, { type: 'array' })
	const sheetName = workbook.SheetNames[0]
	if (!sheetName) return []

	const sheet = workbook.Sheets[sheetName]
	// header: true returns an array of objects keyed by the first row's values
	const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
		header: 1,
		defval: '',
	})

	if (raw.length < 2) return []

	// Detect column indices from the header row (case + accent insensitive)
	const normalize = (s: unknown) =>
		String(s)
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-z0-9]/g, '_')

	const headerRow = raw[0].map(normalize)
	const colFilial = headerRow.indexOf('filial')
	const colBranchCode = headerRow.findIndex((h) =>
		[
			'codigo_filial',
			'filial_codigo',
			'branch_code',
			'codigo_filial_erp',
		].includes(h),
	)
	const colCode = headerRow.findIndex((h) =>
		['codigo_cr', 'code', 'cr_code', 'codigo'].includes(h),
	)
	const colName = headerRow.findIndex((h) =>
		['nome_cr', 'nome', 'name', 'cr_name'].includes(h),
	)

	if (colFilial === -1 || colCode === -1 || colName === -1) return []

	const rows: XlsxRow[] = []
	for (let i = 1; i < raw.length; i++) {
		const row = raw[i]
		const filial = String(row[colFilial] ?? '').trim()
		const codigo_filial =
			colBranchCode === -1 ? '' : String(row[colBranchCode] ?? '').trim()
		const codigo_cr = String(row[colCode] ?? '').trim()
		const nome_cr = String(row[colName] ?? '').trim()
		if (!codigo_cr) continue
		rows.push({ filial, codigo_filial, codigo_cr, nome_cr })
	}
	return rows
}

// ── Diff Computation ──────────────────────────────────────────────────────────

function isSysVirtualBranch(code: string | null | undefined): boolean {
	return (code ?? '').startsWith('SYS-')
}

export function computeOrgDiff(
	branches: OrgBranch[],
	rows: XlsxRow[],
): OrgDiffResult {
	const newBranches: BranchCreatedDiff[] = []
	const renamedBranches: BranchRenamedDiff[] = []
	const renamedCRs: CrRenamedDiff[] = []
	const movedCRs: CrMovedDiff[] = []
	const notFound: CrNotFoundDiff[] = []
	let unchangedCount = 0

	const branchByName = new Map<string, OrgBranch>()
	const branchByCode = new Map<string, OrgBranch>()
	const crByCode = new Map<string, { cr: OrgCR; branch: OrgBranch }>()
	const renamedById = new Set<string>()
	const renamedCrById = new Set<string>()

	const normalizeName = (value: string) => value.trim().toLowerCase()
	const normalizeCode = (value: string | null | undefined) =>
		(value ?? '').trim().toUpperCase()

	for (const branch of branches) {
		branchByName.set(normalizeName(branch.name), branch)
		if (branch.code) branchByCode.set(normalizeCode(branch.code), branch)
		for (const cr of branch.crs) {
			if (cr.code) crByCode.set(cr.code, { cr, branch })
		}
	}

	const newBranchMap = new Map<string, BranchCreatedDiff>()

	for (const row of rows) {
		const existing = crByCode.get(row.codigo_cr)
		if (!existing) {
			notFound.push({
				type: 'CR_NOT_FOUND',
				crCode: row.codigo_cr,
				crName: row.nome_cr,
				filial: row.filial,
			})
			continue
		}

		const { cr, branch: currentBranch } = existing
		const hasCrRename =
			Boolean(row.nome_cr.trim()) &&
			normalizeName(cr.name) !== normalizeName(row.nome_cr)

		if (hasCrRename && !renamedCrById.has(cr.id)) {
			renamedCrById.add(cr.id)
			renamedCRs.push({
				type: 'CR_RENAMED',
				crId: cr.id,
				crCode: cr.code ?? row.codigo_cr,
				fromName: cr.name,
				toName: row.nome_cr,
			})
		}

		const targetByCode = row.codigo_filial
			? branchByCode.get(normalizeCode(row.codigo_filial))
			: undefined
		const targetByName = row.filial
			? branchByName.get(normalizeName(row.filial))
			: undefined
		const targetBranch = targetByCode ?? targetByName

		if (targetBranch && row.filial) {
			const shouldRename =
				normalizeName(targetBranch.name) !== normalizeName(row.filial)
			if (shouldRename && !renamedById.has(targetBranch.id)) {
				renamedById.add(targetBranch.id)
				renamedBranches.push({
					type: 'BRANCH_RENAMED',
					branchId: targetBranch.id,
					branchCode: targetBranch.code ?? null,
					fromName: targetBranch.name,
					toName: row.filial,
				})
			}
		}

		if (targetBranch && currentBranch.id === targetBranch.id) {
			if (!hasCrRename) unchangedCount++
			continue
		}

		if (!targetBranch && !row.filial.trim()) {
			if (!hasCrRename) unchangedCount++
			continue
		}

		let action: CrMovedDiff['action']
		if (!targetBranch) {
			action = { kind: 'MOVE_TO_NEW_VIRTUAL', branchName: row.filial }
			const key = normalizeName(row.filial)
			if (!newBranchMap.has(key)) {
				const diff: BranchCreatedDiff = {
					type: 'BRANCH_CREATED',
					branchName: row.filial,
					crs: [],
				}
				newBranchMap.set(key, diff)
				newBranches.push(diff)
			}
			newBranchMap.get(key)?.crs.push(row)
		} else if (isSysVirtualBranch(targetBranch.code)) {
			action = { kind: 'MOVE_TO_VIRTUAL', targetBranchId: targetBranch.id }
		} else {
			action = { kind: 'UNDO_MOVE' }
		}

		movedCRs.push({
			type: 'CR_MOVED',
			crId: cr.id,
			crCode: cr.code ?? row.codigo_cr,
			crName: cr.name,
			fromBranch: currentBranch.name,
			toBranch: targetBranch?.name ?? row.filial,
			action,
		})
	}

	return {
		newBranches,
		renamedBranches,
		renamedCRs,
		movedCRs,
		notFound,
		unchangedCount,
	}
}

// ── Code Generator ────────────────────────────────────────────────────────────

export function generateBranchCode(name: string): string {
	const slug = name
		.toUpperCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^A-Z0-9]+/g, '-')
		.replace(/^-|-$/g, '')
		.substring(0, 12)
	return `SYS-${slug}`
}
